import axios from 'axios';
import { useCallback, useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import { Link } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { dataGridCol } from '@shared/components/grid/common';
import { formatCurrency, gridDateTimeFormatter } from '@shared/commonFunctions';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { Notistack } from '@shared/components/Notistack/Notistack';
import { AlertModal } from '@shared/components/modals/AlertModal';
import { getError } from '@shared/utils/helpers';
import { ECheckType } from '@shared/interfaces';
import { useServerSide } from '@shared/hooks/useServerSide';
import { ActionButtonWrapper } from '@styles/CheckerMenu/CheckRequestStyles';
import { Wrapper } from '@styles/CommonStyles';
import { DealsButton } from '@styles/StyledButtons';
import { ECheckRequest, CheckStatus, IActionButtonProps, ICheckRequest } from './interfaces';
import { CheckRequestAPI, requestProcessCheck } from './requests';
import { CheckRequestModal } from './CheckRequestModal';
import { apiPrefix, checkStatus, EButtonAction } from './constants';
import { CustomCheckRequestModal } from './CustomCheckRequest/CustomCheckRequestModal';

const ActionButton = ({ onClick, title }: IActionButtonProps) => (
  <DealsButton
    type="button"
    onClick={onClick}
  >
    {title}
  </DealsButton>
);

let checks: ICheckRequest[] | [] = [];

const extraColumnsForProductType = [
  dataGridCol('productType'),
  dataGridCol('provider'),
  dataGridCol('customerRefundAmount', { minWidth: 170, valueFormatter: ({ value }) => formatCurrency(value) }),
  dataGridCol('sellerRefundAmount', { minWidth: 150, valueFormatter: ({ value }) => formatCurrency(value) }),
];

interface ICheckReq {
  checkRequestType: ECheckRequest;
  configurationSubGroup?: string;
}

export const CheckRequest = ({ checkRequestType, configurationSubGroup }: ICheckReq) => {
  const { requestPayload, onSortModelChange, onFilterModelChange, onPageChange, handleClosePanel } = useServerSide();

  const [selectedCheckReq, setSelectedChecksReq] = useState<ICheckRequest[] | []>([]);
  const [rerender, setRerender] = useState(false);
  const [openConfirmActionModal, setOpenConfirmActionModal] = useState(false);
  const [openCustomCheckModal, setOpenCustomCheckModal] = useState(0);
  const [checkType, setCheckType] = useState<string>('');
  const [action, setAction] = useState<string>('');
  const [checkReq, setCheckReq] = useState<ICheckRequest>();
  const [checkRequestModalData, setCheckRequestModalData] = useState<ICheckRequest | null>(null);
  const [checkRequestModalPosition, setCheckRequestModalPosition] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [refreshCheckRequest, setRefreshCheckRequest] = useState(0);

  const getCheckReqURL = useMemo(
    () =>
      `${apiPrefix}/configurationGroup/ACCOUNTING_MENU/configurationSubGroup/${checkStatus[
        configurationSubGroup as keyof typeof checkStatus
      ]?.status}`,
    [configurationSubGroup]
  );

  const {
    data: checkRequestList,
    mutate,
    isValidating,
    error,
  } = useSWR(getCheckReqURL, (url: string) => axios.post(url, { ...requestPayload, pageSize: 20 }), { revalidateOnMount: false });

  const checkRequests = useMemo(
    () =>
      checkRequestList?.data?.results?.map((item: ICheckRequest) => ({
        ...item,
        dealNumericId: item.dealNumericId ? item.dealNumericId : item?.legacyDealId || '',
        checkRequestType: String(checkRequestType),
      })),
    [checkRequestList, checkRequestType]
  );

  useEffect(() => {
   mutate();
  }, [getCheckReqURL, requestPayload, refreshCheckRequest]);

  useEffect(() => {
    if (checkRequestList) {
      setTotalCount(checkRequestList?.data?.totalCount);
    }
  }, [checkRequestList]);

  const convertCheckRequest = useCallback((checkType: ECheckType, action: EButtonAction, checkReq: ICheckRequest) => {
    setCheckType(checkType);
    setAction(action);
    setCheckReq(checkReq);
  }, []);

  const handleCutAllSelection = useCallback(async () => {
    const promises: Promise<void>[] = [];

    for (const check of checks) {
      check.checkStatus = 'CheckCut';
      const path = `${checkStatus[ECheckType.Cut].apiURL}/${check.requestId}`;
      promises.push(CheckRequestAPI.PATCH(path, check, checkRequestType));
    }

    try {
      await Promise.all(promises);
      Notistack.infoMessage('Success check cut');
      mutate();
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  }, [selectedCheckReq, checks, checkStatus, checkRequestType]);

  const handleSubmitAllSelection = useCallback(async () => {
    const promises: Promise<void>[] = [];

    for (const check of checks) {
      const data = {
        checkRequestId: check?.requestId,
        dealId: check?.dealId,
        checkStatus: CheckStatus[checkReq?.checkStatus as keyof typeof CheckStatus],
        checkRequestType: Number(checkReq?.checkRequestType),
      };
      promises.push(requestProcessCheck(data));
    }

    try {
      await Promise.all(promises);
      Notistack.infoMessage('Success check submission');
      mutate();
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  }, [selectedCheckReq, checks, checkStatus, checkRequestType, checkReq]);

  const handleCut = useCallback(
    (checkReq: ICheckRequest, isSubmit: boolean) => {
      setOpenConfirmActionModal(true);
      checkReq.checkStatus = 'CheckCut';
      if (isSubmit) convertCheckRequest(ECheckType.Cut, EButtonAction.Submit, checkReq);
      else convertCheckRequest(ECheckType.Cut, EButtonAction.Cut, checkReq);
    },
    [convertCheckRequest]
  );

 

  const handleDecline = useCallback(
    (checkReq: ICheckRequest) => {
      setOpenConfirmActionModal(true);
      checkReq.checkStatus = 'Declined';
      convertCheckRequest(ECheckType.Declined, EButtonAction.Decline, checkReq);
    },
    [convertCheckRequest]
  );

  const handleUndo = useCallback(
    (checkReq: ICheckRequest) => {
      setOpenConfirmActionModal(true);
      convertCheckRequest(ECheckType.Undo, EButtonAction.Undo, checkReq);
    },
    [convertCheckRequest]
  );
  
  const handleRowClick =  useCallback((params: GridRenderCellParams) => {
    setCheckRequestModalData(params.row);
    setCheckRequestModalPosition(prevState => prevState + 1);
  },[]);

  const handeCloseCheckRequestModal = useCallback(() => {
    setCheckRequestModalData(null);
    setCheckRequestModalPosition(0);
  }, []);
  
  const baseColumns: GridColDef[] = useMemo(
    () => [
      dataGridCol('requestNumericId', {
        headerName: 'Check Id',
        flex: 1,
        minWidth: 80,
        type: 'string',
        renderCell: (params) => <Link onClick={() => handleRowClick(params)}>{params.row.requestNumericId}</Link>,
      }),
      dataGridCol('dealNumericId', {
        headerName: 'Deal Id',
        type: 'string',
        flex: 1,
        minWidth: 80,
      }),
      dataGridCol('requestedDate', {
        headerName: 'Req Date',
        type: 'dateTime',
        flex: 1,
        minWidth: 105,
        valueFormatter: gridDateTimeFormatter,
      }),
      dataGridCol('requestedBy', {
        headerName: 'Req By',
        type: 'string',
        flex: 1.5,
        minWidth: 100,
      }),
      dataGridCol('customerName', {
        headerName: 'Customer',
        type: 'string',
        flex: 1.5,
        minWidth: 100,
      }),
      dataGridCol('loanConsultant', {
        headerName: 'LC',
        type: 'string',
        flex: 1.3,
        minWidth: 100,
      }),
      dataGridCol('payeeName', {
        headerName: 'Payee',
        type: 'string',
        flex: 1,
        minWidth: 100,
      }),
      dataGridCol('amountRequested', {
        headerName: 'Amt Req',
        type: 'number',
        flex: 1,
        minWidth: 100,
        valueFormatter: ({ value }) => formatCurrency(value),
      }),
      dataGridCol('statusCD', {
        headerName: 'Status',
        type: 'string',
        flex: 1,
        minWidth: 100,
      }),
      dataGridCol('reason', {
        type: 'string',
        flex: 1,
        minWidth: 120,
      }),
      dataGridCol('assetDesc', {
        headerName: 'Asset Tag',
        type: 'string',
        flex: 1,
        minWidth: 100,
      }),
    ],
    []
  );

  const aTRefundCheckColumns: GridColDef[] = useMemo(
    () => [
      dataGridCol('dealNumericId', {
        headerName: 'Deal Id',
        type: 'string',
      }),
      dataGridCol('payoffSubmittedDate', {
        headerName: 'Payoff Submitted Date',
        type: 'dateTime',
        width: 231,
        valueFormatter: gridDateTimeFormatter,
      }),
      dataGridCol('customerName', {
        headerName: 'Customer',
        type: 'string',
        width: 150,
      }),
      dataGridCol('loanConsultant', {
        headerName: 'LC',
        type: 'string',
        width: 150,
      }),
      dataGridCol('customerAddress', {
        headerName: 'Customer Address',
        type: 'string',
        width: 231,
      }),
      dataGridCol('vin', {
        headerName: 'VIN',
        type: 'string',
        width: 200,
        valueFormatter: ({ value }) => (value?.toUpperCase()),
      }),
      dataGridCol('customerRefundAmount', {
        headerName: 'Refund Amount',
        type: 'number',
        width: 150,
        valueFormatter: ({ value }) => formatCurrency(value),
      }),
    ],
    []
  );

  const findPage = useMemo(
    () => configurationSubGroup?.split('-')?.[checkRequestType === ECheckRequest.Check ? 1 : 3],
    [configurationSubGroup, checkRequestType]
  );

  const actions = useMemo(() => {
    if (checkRequestType === ECheckRequest.AtRefundCheck) {
      return [       

      ];
    }
    return [
      dataGridCol('actions', {
        width: 180,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          if (findPage === EButtonAction.Cut) {
            return (
              <ActionButtonWrapper>
                <ActionButton
                  title="Decline"
                  onClick={() => handleDecline(params.row)}
                />
                <ActionButton
                  title="Undo"
                  onClick={() => handleUndo(params.row)}
                />
              </ActionButtonWrapper>
            );
          } else if (findPage === EButtonAction.Declined) {
            return (
              <ActionButtonWrapper>
                <ActionButton
                  title="Cut"
                  onClick={() => handleCut(params.row, false)}
                />
                <ActionButton
                  title="Undo"
                  onClick={() => handleUndo(params.row)}
                />
              </ActionButtonWrapper>
            );
          }
          return (
            <ActionButtonWrapper>
              <ActionButton
                title="Cut"
                onClick={() => handleCut(params.row, false)}
              />
              <ActionButton
                title="Decline"
                onClick={() => handleDecline(params.row)}
              />
            </ActionButtonWrapper>
          );
        },
      }),
    ];
  }, [handleCut, handleDecline, handleUndo, configurationSubGroup]);

  const columns = useMemo(() => {
    if (checkRequestType === ECheckRequest.Product) {
      return [...baseColumns, ...extraColumnsForProductType, ...actions];
    } else if (checkRequestType === ECheckRequest.AtRefundCheck) return [...aTRefundCheckColumns, ...actions];

    return [...baseColumns, ...actions];
  }, [checkRequestType, actions]);

  const submitCheckRequest = useCallback(async () => {
    const path = `${checkStatus[checkType as keyof typeof checkStatus].apiURL}/${checkReq?.requestId}`;
    const isCutDeclineSubmit = [EButtonAction.Cut, EButtonAction.Decline, EButtonAction.Submit].includes(action as EButtonAction);

    try {
      if (isCutDeclineSubmit) {
          if (checks.length > 1) await handleSubmitAllSelection();
          else {
            const data = {
              checkRequestId: checkReq?.requestId,
              dealId: checkReq?.dealId,
              checkStatus: CheckStatus[checkReq?.checkStatus as keyof typeof CheckStatus],
              checkRequestType: Number(checkReq?.checkRequestType),
            };

          await requestProcessCheck(data);
        }
      } else {
        await checkStatus[checkType as keyof typeof checkStatus].fn(path, checkReq as ICheckRequest, checkRequestType);
      }

      Notistack.infoMessage(`Success check ${action}`);
      setOpenConfirmActionModal(false);
      setRerender(!rerender);
      mutate();
    } catch (err) {
      Notistack.generalError(getError(err));
      setOpenConfirmActionModal(false);
      setRerender(!rerender);
      mutate();
    }
  }, [checkStatus, checkType, checkReq, action, requestProcessCheck, checkRequestType, mutate]);

  const buttonsGroup = useMemo(() => {
    if (checkRequestType === ECheckRequest.AtRefundCheck) { 
      return (<></>) ;   
    }
    return (
      <>
        {checkRequestType === ECheckRequest.Product && (
          <DealsButton
            size="medium"
            onClick={() => setOpenCustomCheckModal(prevState => prevState + 1)}
          >
            Create Custom Check
          </DealsButton>
        )}
          <DealsButton
            size="medium"
            disabled={!selectedCheckReq?.length}
            onClick={handleCutAllSelection}
          >
            Cut All Selected
          </DealsButton>               
      </>
    );
  }, [checkRequestType, selectedCheckReq, handleCutAllSelection]);

  return (
    <>
      <Wrapper>
        <PhoenixGrid
          autoHeight
          withHeader
          strict
          checkboxSelection
          noButton
          disableSelectionOnClick
          showClearAllFilter
          pageSize={20}
          headerContent={buttonsGroup}
          name={configurationSubGroup?.replaceAll('-', ' ')}
          columns={columns}
          loading={(!checkRequests && !error) || isValidating}
          rows={checkRequests && !isValidating ? checkRequests : []}
          rowCount={totalCount}
          paginationMode="server"
          sortingMode="server"
          filterMode="server"
          getRowId={(row) => row.requestId}
          onSortModelChange={onSortModelChange}
          onFilterModelChange={onFilterModelChange}
          onPageChange={onPageChange}
          onPreferencePanelClose={handleClosePanel}
          onSelectionModelChange={(row, details) => {
            checks = Array.from(details.api.getSelectedRows().values());
            setSelectedChecksReq(checks);
          }}
        />
      </Wrapper>

      <CheckRequestModal
        open={!!checkRequestModalData}
        checkRequestModalPosition={checkRequestModalPosition}
        modalType={configurationSubGroup as ECheckType}
        requestRowData={checkRequestModalData}
        checkRequestType={checkRequestType}
        handleClose={handeCloseCheckRequestModal}
      />

      <CustomCheckRequestModal
        customCheckRequestModalPosition={openCustomCheckModal}
        handleClose={() => setOpenCustomCheckModal(0)}
        refreshCheckRequest={setRefreshCheckRequest}
        configurationSubGroup={configurationSubGroup}
      />

      <AlertModal
        title="Alert"
        textSize="20px"
        subtitle={`Confirm ${action.toUpperCase()} check${checks.length > 1 ? 's' : ''} ?`}
        cancelButtonText="Cancel"
        buttonText="Ok"
        marginTop={0}
        open={openConfirmActionModal}
        onConfirm={submitCheckRequest}
        onClose={() => setOpenConfirmActionModal(false)}
      />
    </>
  );
};
